import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LuEllipsisVertical } from 'react-icons/lu';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/Init';
import AppLayout from '../components/oracle/AppLayout';
import LogoSpinner from '../components/oracle/LogoSpinner';
import HeaderSubtitle from '../components/oracle/HeaderSubtitle';
import HeaderTitle from '../components/oracle/HeaderTitle';
import HairlineDivider from '../components/oracle/HairlineDivider';
import PortraitComparison from '../components/oracle/PortraitComparison';
import VectorRow from '../components/oracle/VectorRow';
import VectorPill from '../components/oracle/VectorPill';
import AurumButton from '../components/oracle/AurumButton';
import EmptyState from '../components/oracle/EmptyState';
import { vectorGlyph } from '../components/oracle/VectorGlyphs';
import { ROUTES } from '../contracts/routes';
import { COPY } from '../contracts/copyMap';
import { HOLISTIC_TARGET, glowupVectorLabel } from '../contracts/glowupApi';
import { isPaywalled } from '../contracts/billingApi';
import MembershipCTA from '../components/billing/MembershipCTA';

// Own-glowup view per DESIGN.md §16.3.
//
// Fetches the peer-score readout for this glowup AND finds the matching
// OwnGlowupRow (via listOwnGlowups) so the BEFORE/AFTER thumbnails and the
// timespan label can be rendered. Both are parallel calls — peer-score
// drives the gated path (quota check); the own-glowup row is best-effort
// (if it fails, the comparison stays empty but the page still renders).

const MONTH_MS = 1000 * 60 * 60 * 24 * 30;

function timespanLabel(beforeIso, afterIso) {
  if (!beforeIso || !afterIso) return null;
  const a = new Date(beforeIso).getTime();
  const b = new Date(afterIso).getTime();
  if (!a || !b || b <= a) return null;
  const months = Math.round((b - a) / MONTH_MS);
  if (months >= 12) {
    const years = Math.floor(months / 12);
    const rem = months % 12;
    if (rem === 0) return `${years} YEAR`;
    return `${years} YEAR ${rem} MONTH`;
  }
  return `${months} MONTH`;
}

function vectorLabel(target) {
  if (target === HOLISTIC_TARGET) return 'OVERALL';
  return glowupVectorLabel(target);
}

const GlowupDetail = () => {
  const { glowup_id: glowupId } = useParams();
  const navigate = useNavigate();
  const [peerState, setPeerState] = useState({ phase: 'loading' });
  const [ownRow, setOwnRow] = useState(null);
  const [kebabOpen, setKebabOpen] = useState(false);

  const loadPeer = useCallback(async () => {
    setPeerState({ phase: 'loading' });
    try {
      const get = httpsCallable(functions, 'getGlowupPeerScore');
      const result = await get({ glowup_id: glowupId });
      const data = result && result.data;
      if (data && data.status === 'success') {
        setPeerState({ phase: 'ready', peerScore: data.peer_score });
        return;
      }
      if (isPaywalled(data)) {
        setPeerState({ phase: 'paywalled' });
        return;
      }
      if (data && data.error_code === 'QUOTA_REQUIRED') {
        setPeerState({ phase: 'quota' });
        return;
      }
      setPeerState({
        phase: 'error',
        message: (data && data.error_message) || COPY.REJECT_NETWORK_SUBTITLE,
      });
    } catch (err) {
      setPeerState({
        phase: 'error',
        message: (err && err.message) || COPY.REJECT_NETWORK_SUBTITLE,
      });
    }
  }, [glowupId]);

  const loadOwnRow = useCallback(async () => {
    try {
      const list = httpsCallable(functions, 'listOwnGlowups');
      const result = await list({});
      const data = result && result.data;
      if (data && data.status === 'success') {
        const row = (data.entries || []).find((e) => e.glowup_id === glowupId);
        if (row) setOwnRow(row);
      }
    } catch {
      // best-effort — page still renders without the comparison images
    }
  }, [glowupId]);

  useEffect(() => {
    loadPeer();
    loadOwnRow();
  }, [loadPeer, loadOwnRow]);

  if (peerState.phase === 'loading') {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <LogoSpinner />
        </div>
      </AppLayout>
    );
  }
  if (peerState.phase === 'paywalled') {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-8 py-12">
          <MembershipCTA reason="glowup" />
        </div>
      </AppLayout>
    );
  }
  if (peerState.phase === 'error') {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-8 py-12">
          <EmptyState
            headline="UNABLE TO LOAD"
            subtitle={peerState.message}
            ctaLabel={COPY.RETRY_CTA}
            onCta={loadPeer}
          />
        </div>
      </AppLayout>
    );
  }
  if (peerState.phase === 'quota') {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-8 py-12">
          <EmptyState
            headline={COPY.GLOWUP_PEER_QUOTA_LOCKED}
            subtitle={COPY.JUDGE_SUBTITLE}
            ctaLabel={COPY.JUDGE_SUBMIT_CTA}
            onCta={() => navigate(ROUTES.JUDGE)}
          />
        </div>
      </AppLayout>
    );
  }

  const ts = ownRow ? timespanLabel(ownRow.before_taken_at, ownRow.after_taken_at) : null;
  const headline = ts
    ? `${ts} ${COPY.GLOWUP_OWN_TIMESPAN_SUFFIX}`
    : COPY.GLOWUP_DETAIL_HEADLINE;
  const subtitle = ts
    ? `Your ${ts.toLowerCase().split(' ').slice(0, 2).join(' ')} glowup`
    : 'Your glowup';

  const sortedVectors = (peerState.peerScore && peerState.peerScore.vectors)
    ? [...peerState.peerScore.vectors].sort((a, b) => b.mentions - a.mentions)
    : [];

  return (
    <AppLayout
      header={(
        <header className="flex items-start justify-between gap-6">
          <div className="flex flex-col min-w-0">
            <HeaderTitle
              as="h2"
              className="font-display leading-[1.05]"
              size="text-[64px]"
              sizeScrolled="text-[44px]"
            >
              {headline}
            </HeaderTitle>
            <HeaderSubtitle
              className="font-display italic text-bone-dim mt-4"
              style={{ fontSize: 16 }}
            >
              {subtitle}
            </HeaderSubtitle>
            {peerState.peerScore && peerState.peerScore.biggest_vector && (
              <div className="mt-4">
                <VectorPill variant="qualitative">
                  BIGGEST: {vectorLabel(peerState.peerScore.biggest_vector)}
                </VectorPill>
              </div>
            )}
          </div>
          <KebabMenu open={kebabOpen} onToggle={() => setKebabOpen((v) => !v)} />
        </header>
      )}
    >
      <div className="flex flex-col gap-8 px-5 lg:px-12 pb-10 lg:pb-12">
        <HairlineDivider />

        <PortraitComparison
          before={{
            imageUrl: ownRow && ownRow.before_thumbnail_url,
            score: null,
            dateLabel: ownRow && formatDate(ownRow.before_taken_at),
            alt: 'your before',
          }}
          after={{
            imageUrl: ownRow && ownRow.after_thumbnail_url,
            score: null,
            dateLabel: ownRow && formatDate(ownRow.after_taken_at),
            alt: 'your after',
          }}
          className="max-w-3xl"
        />

        <section
          aria-label={COPY.GLOWUP_OWN_RECEIVED_HEADER}
          className="flex flex-col gap-3 max-w-2xl"
        >
          <h2
            className="font-display uppercase tracking-[0.10em] text-bone m-0"
            style={{ fontSize: 13 }}
          >
            {COPY.GLOWUP_OWN_RECEIVED_HEADER}
          </h2>
          {sortedVectors.length === 0 ? (
            <p
              className="font-display italic text-bone-dim m-0"
              style={{ fontSize: 14 }}
            >
              {COPY.GLOWUP_OWN_NO_ENCOURAGEMENT}
            </p>
          ) : (
            <div className="flex flex-col bg-vein border border-hairline-soft">
              {sortedVectors.map((v, i) => (
                <VectorRow
                  key={v.name}
                  icon={vectorGlyph(v.name)}
                  name={vectorLabel(v.name)}
                  description={`${v.mentions} ${v.mentions === 1 ? 'mention' : 'mentions'}`}
                  value={v.mean_score != null ? v.mean_score.toFixed(1) : null}
                  divider={i < sortedVectors.length - 1}
                />
              ))}
            </div>
          )}
        </section>

        <div className="flex justify-center mt-4">
          <AurumButton onClick={() => navigate(ROUTES.GLOWUPS)}>
            {COPY.RETURN_CTA}
          </AurumButton>
        </div>
      </div>
    </AppLayout>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const KebabMenu = ({ open, onToggle }) => (
  <div className="relative">
    <button
      type="button"
      onClick={onToggle}
      aria-label={COPY.GLOWUP_OWN_KEBAB_LABEL}
      aria-expanded={open}
      className="bg-transparent border-0 p-2 text-bone-dim hover:text-bone cursor-pointer"
    >
      <LuEllipsisVertical size={18} aria-hidden="true" />
    </button>
    {open && (
      <motion.div
        role="menu"
        className="absolute right-0 top-full mt-1 w-48 bg-vein-raised border border-hairline-soft rounded-field py-2 z-30 shadow-card-lift overflow-hidden"
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18, ease: [0.65, 0, 0.35, 1] }}
      >
        <KebabItem>{COPY.GLOWUP_OWN_KEBAB_SHARE}</KebabItem>
        <KebabItem>{COPY.GLOWUP_OWN_KEBAB_DELETE}</KebabItem>
        <KebabItem>{COPY.GLOWUP_OWN_KEBAB_REPORT}</KebabItem>
      </motion.div>
    )}
  </div>
);

const KebabItem = ({ children }) => (
  <button
    type="button"
    role="menuitem"
    className="block w-full text-left bg-transparent border-0 px-4 py-2 font-display text-bone hover:bg-vein cursor-pointer"
    style={{ fontSize: 13 }}
  >
    {children}
  </button>
);

export default GlowupDetail;
